/**
 * PWA Composable
 * Provides PWA installation, update, and status functionality
 */
import { ref, computed } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { PWA_STANDALONE_USED_KEY } from '@/constants/pwaStorage'
import { PwaUpdateCoordinator } from '@/services/pwaUpdateCoordinator'

// Install prompt event
let deferredPrompt: BeforeInstallPromptEvent | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Track if app is installable
const isInstallable = ref(false)
const isInstalled = ref(false)
const isOnline = ref(navigator.onLine)

function recordStandaloneSessionHint(): void {
  if (typeof window === 'undefined') return
  try {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    if (standalone) {
      localStorage.setItem(PWA_STANDALONE_USED_KEY, '1')
    }
  } catch {
    /* private mode / quota */
  }
}

/** True if user has opened this origin in standalone (home screen) — hide iOS Safari install nudge. */
export function hasUserOpenedInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(PWA_STANDALONE_USED_KEY) === '1'
  } catch {
    return false
  }
}

// Listen for install prompt
if (typeof window !== 'undefined') {
  recordStandaloneSessionHint()

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    isInstallable.value = true
    console.log('[PWA] App is installable')
  })

  // Track installation
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    isInstallable.value = false
    isInstalled.value = true
    try {
      localStorage.setItem(PWA_STANDALONE_USED_KEY, '1')
    } catch {
      /* ignore */
    }
    console.log('[PWA] App was installed')
  })

  // Track online/offline status
  window.addEventListener('online', () => {
    isOnline.value = true
    console.log('[PWA] Online')
  })

  window.addEventListener('offline', () => {
    isOnline.value = false
    console.log('[PWA] Offline')
  })
}

// SINGLE service-worker registration for the whole app. usePWA() is invoked by
// App.vue, the always-mounted PWAPrompt, and SettingsView; calling
// useRegisterSW() per-invocation created duplicate registrations and duplicate
// pollers (#517 D1). Register once at module scope and share the result.
let swRegistration: ServiceWorkerRegistration | undefined
let coordinator: PwaUpdateCoordinator | null = null

// #517 Release 2: when the coordinator activates a waiting SW (SKIP_WAITING),
// the browser fires `controllerchange` once the new SW takes control. THAT is
// when we reload — so the swap and reload are atomic. Guard against the
// duplicate controllerchange some browsers emit, and against the FIRST-install
// controllerchange (no prior controller) which must NOT reload a fresh load.
let controllerChangeReloadDone = false
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  const hadControllerAtLoad = Boolean(navigator.serviceWorker.controller)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (controllerChangeReloadDone) return
    // First install (no controller when the page loaded): the new SW claiming
    // control is not an UPDATE — don't reload.
    if (!hadControllerAtLoad) return
    controllerChangeReloadDone = true
    window.location.reload()
  })
}

/**
 * Tell a WAITING service worker to activate. Returns true if one existed (so the
 * caller waits for controllerchange to reload), false otherwise (caller falls
 * back to a nav reload). #517 Release 2.
 */
async function activateWaitingWorker(): Promise<boolean> {
  const reg = swRegistration ?? (await navigator.serviceWorker?.getRegistration())
  const waiting = reg?.waiting
  if (!waiting) return false
  waiting.postMessage({ type: 'SKIP_WAITING' })
  return true
}

const {
  needRefresh,
  offlineReady,
  updateServiceWorker,
} = useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, registration) {
    console.log('[PWA] Service Worker registered:', swUrl)
    swRegistration = registration

    // The Update Coordinator (#517) owns update detection + reload in production.
    // It checks by build ID (catches byte-identical SW with changed bundle) and
    // on foreground (iOS, where hourly timers die backgrounded).
    if (!import.meta.env.DEV && !coordinator) {
      coordinator = new PwaUpdateCoordinator({
        updateRegistration: async () => {
          if (!swRegistration) return
          try {
            await swRegistration.update()
          } catch (error) {
            // iOS/WKWebView invalidates the SW registration when backgrounded
            // (InvalidStateError). Don't permanently skip — drop this attempt;
            // the coordinator re-checks on the next foreground event.
            if (error instanceof DOMException && error.name === 'InvalidStateError') {
              console.warn('[PWA] SW registration stale (iOS background), will retry on next foreground')
              return
            }
            console.error('[PWA] SW update check failed:', error)
          }
        },
        activateWaitingWorker,
      })
      void coordinator.start()
    }
  },
  onRegisterError(error) {
    console.error('[PWA] Service Worker registration error:', error)
  },
  onNeedRefresh() {
    console.log('[PWA] New content available, refresh needed')
  },
  onOfflineReady() {
    console.log('[PWA] App ready for offline use')
  },
})

export function usePWA() {
  // Standalone can become true after first paint; re-check when composable runs (e.g. App.vue).
  recordStandaloneSessionHint()

  // Check if running in standalone mode (installed PWA)
  const isStandalone = computed(() => {
    if (typeof window === 'undefined') return false
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    )
  })

  // Trigger app installation
  async function installApp(): Promise<boolean> {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available')
      return false
    }

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      console.log('[PWA] Install prompt outcome:', outcome)

      if (outcome === 'accepted') {
        deferredPrompt = null
        isInstallable.value = false
        return true
      }
      return false
    } catch (error) {
      console.error('[PWA] Install error:', error)
      return false
    }
  }

  // Update the app (reload with new SW)
  async function updateApp() {
    await updateServiceWorker(true)
  }

  // Dismiss update notification
  function dismissUpdate() {
    needRefresh.value = false
  }

  /**
   * Set the app badge (notification count on app icon)
   * Supported on installed PWAs on Android, iOS, Windows, macOS
   */
  async function setBadge(count: number): Promise<boolean> {
    // Check if Badge API is supported
    if (!('setAppBadge' in navigator)) {
      console.log('[PWA] Badge API not supported')
      return false
    }

    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count)
        console.log('[PWA] Badge set to:', count)
      } else {
        await (navigator as any).clearAppBadge()
        console.log('[PWA] Badge cleared')
      }
      return true
    } catch (error) {
      console.error('[PWA] Failed to set badge:', error)
      return false
    }
  }

  /**
   * Clear the app badge
   */
  async function clearBadge(): Promise<boolean> {
    return setBadge(0)
  }

  return {
    // State
    needRefresh,
    offlineReady,
    isInstallable,
    isInstalled,
    isStandalone,
    isOnline,

    // Actions
    installApp,
    updateApp,
    dismissUpdate,
    setBadge,
    clearBadge,
  }
}
